import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { tripsApi, podsApi, scanningApi, shouldQueueOffline, type PodDocument, type Stop } from '@/api/client';
import { useAsync } from '@/hooks';
import { CrewLayout } from '@/components/Layout';
import { Nav } from '@/components/Nav';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { OfflineIndicator } from '@/offline';
import {
  queueEvent,
  cacheCrewTrip,
  getCachedCrewTrip,
  getQueuedEvents,
  OFFLINE_QUEUE_CHANGED,
} from '@/offline/queue';
import { newCrewActionMeta } from '@/lib/offlineReplay';
import { useToast } from '@/components/Feedback';
import { formatDateTime, stopTypeLabel } from '@/lib/format';
import { aggregateProgress } from '@/lib/scanReconcile';
import { SignaturePad, type SignaturePadHandle } from '@/components/SignaturePad';
import { compressImage } from '@/lib/imageCompression';
import { crewNavItems } from '@/pages/navItems';
import type { Trip } from '@/api/client';
import { CrewActionBar, CrewIcon, CrewMessage } from '@/components/CrewUi';

function signaturePadHeight(): number {
  const h = typeof window === 'undefined' ? 640 : window.innerHeight;
  return Math.max(150, Math.min(260, Math.round(h - 460)));
}

export function CrewPod() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const queryStopId = searchParams.get('stopId') ?? '';

  const { data: trip } = useAsync(async () => {
    try {
      const current = await tripsApi.myCurrent();
      await cacheCrewTrip(current as unknown as Record<string, unknown> | null);
      return current;
    } catch (err) {
      const cached = await getCachedCrewTrip<Trip>();
      if (cached) return cached;
      throw err;
    }
  }, []);
  const [selectedStopId, setSelectedStopId] = useState(queryStopId);
  const [pod, setPod] = useState<PodDocument | null | undefined>(undefined);
  const [podLoading, setPodLoading] = useState(false);

  const [signatoryName, setSignatoryName] = useState('');
  const [piecesDelivered, setPiecesDelivered] = useState('');
  const [photoKey, setPhotoKey] = useState('');
  const [photoLoading, setPhotoLoading] = useState(false);
  const [queuedPodStopIds, setQueuedPodStopIds] = useState<string[]>([]);
  // Cargo check counts saved on the phone (offline) by stop id, for the pieces prefill.
  const queuedCargoCountsRef = useRef<Record<string, number>>({});
  const [showCorrectionForm, setShowCorrectionForm] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const sigPadRef = useRef<SignaturePadHandle>(null);
  // Size the pad so it stays fully visible above the sticky Save button on a 360×640 phone.
  const [padHeight, setPadHeight] = useState(() => signaturePadHeight());
  useEffect(() => {
    const onResize = () => setPadHeight(signaturePadHeight());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const stops = trip?.stops ?? [];
  const stopsRef = useRef<Stop[]>([]);
  stopsRef.current = stops;
  const eligibleStops = stops.filter((s: Stop) => s.type !== 'return_to_base');
  const activeStopId = selectedStopId || eligibleStops.find((s) => s.status !== 'pending')?.id || eligibleStops[0]?.id || '';

  useEffect(() => {
    if (queryStopId) setSelectedStopId(queryStopId);
  }, [queryStopId]);

  // Signatures saved on the phone (offline) but not uploaded yet — don't ask for them twice.
  useEffect(() => {
    const refresh = () => {
      getQueuedEvents()
        .then((events) => {
          setQueuedPodStopIds(
            events
              .filter((e) => e.type === 'pod' && e.status !== 'failed')
              .map((e) => String(e.payload.tripStopId)),
          );
          const counts: Record<string, number> = {};
          for (const e of events) {
            if (e.type === 'cargo_check' && e.status !== 'failed' && typeof e.payload.countedPieces === 'number') {
              counts[String(e.payload.stopId)] = e.payload.countedPieces;
            }
          }
          queuedCargoCountsRef.current = counts;
        })
        .catch(() => setQueuedPodStopIds([]));
    };
    refresh();
    window.addEventListener(OFFLINE_QUEUE_CHANGED, refresh);
    return () => window.removeEventListener(OFFLINE_QUEUE_CHANGED, refresh);
  }, []);
  const podQueued = !!activeStopId && queuedPodStopIds.includes(activeStopId);

  useEffect(() => {
    if (!activeStopId) {
      setPod(undefined);
      return;
    }
    let cancelled = false;
    setPodLoading(true);
    podsApi
      .byStop(activeStopId)
      .then(async (result) => {
        if (cancelled) return;
        setPod(result);
        if (result) {
          setPiecesDelivered(String(result.piecesDelivered));
          return;
        }
        // Prefill pieces from the cargo check count (no-manifest stops), else from what
        // was scanned at this stop (crew can still edit).
        setPiecesDelivered('');
        const cargoCount =
          stopsRef.current.find((s) => s.id === activeStopId)?.cargoCheck?.countedPieces ??
          queuedCargoCountsRef.current[activeStopId];
        if (typeof cargoCount === 'number') {
          setPiecesDelivered(String(cargoCount));
          return;
        }
        try {
          const counts = await scanningApi.counts(activeStopId);
          const scanned = aggregateProgress(counts).scanned;
          if (!cancelled && scanned > 0) {
            setPiecesDelivered((prev) => prev || String(scanned));
          }
        } catch {
          /* no manifest / offline — leave blank */
        }
      })
      .catch(() => {
        if (!cancelled) setPod(null);
      })
      .finally(() => {
        if (!cancelled) setPodLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeStopId]);

  const isSigned = !!pod?.signatureImageKey;

  const handlePhotoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Compress before upload — mobile photos are often several MB; capping the
    // longest edge at 1600px and re-encoding at JPEG q0.7 keeps this small over cellular.
    setPhotoLoading(true);
    setError('');
    try {
      setPhotoKey(await compressImage(file));
    } catch {
      setPhotoKey('');
      setError('Could not read photo — try taking it again.');
    } finally {
      setPhotoLoading(false);
    }
  };

  const resetForm = () => {
    setSignatoryName('');
    setPhotoKey('');
    sigPadRef.current?.clear();
    setShowCorrectionForm(false);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!activeStopId || !signatoryName.trim()) return;
    if (photoLoading) return;
    if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
      setError('Ask the receiver to sign in the white box.');
      return;
    }
    const signatureImageKey = sigPadRef.current.toDataURL();

    setSubmitLoading(true);
    // Same key/time for the online attempt and any offline replay (no duplicate POD).
    const action = newCrewActionMeta();
    try {
      if (!pod) {
        // No POD exists for this stop yet — generate seals the immutable hash with the
        // signature already baked in, in one step, and triggers pay accrual immediately.
        if (!piecesDelivered) {
          setError('Pieces delivered is required.');
          return;
        }
        const created = await podsApi.generate({
          tripStopId: activeStopId,
          signatoryName: signatoryName.trim(),
          piecesDelivered: Number(piecesDelivered),
          signatureImageKey,
          signedStatePhotoKey: photoKey || undefined,
        }, action);
        setPod(created);
        resetForm();
        toast(`Signature saved${created.podNumber ? ` (${created.podNumber})` : ''}`);
        navigate('/crew');
        return;
      } else {
        // Existing POD: captureSignature seals it if unsigned, or — if it's already
        // sealed — files a brand-new correction POD linked via correctsPodId rather than
        // mutating the original.
        const result = await podsApi.captureSignature(pod.id, {
          signatoryName: signatoryName.trim(),
          signatureImageKey,
          signedStatePhotoKey: photoKey || undefined,
        }, action);
        setPod(result);
        resetForm();
        if (!result.correctsPodId) {
          toast('Signature saved');
          navigate('/crew');
          return;
        }
        setMessage('Correction saved. The first signature is still kept on record.');
      }
      resetForm();
    } catch (err) {
      if (shouldQueueOffline(err) && !pod) {
        try {
          await queueEvent({
            type: 'pod',
            payload: {
              tripStopId: activeStopId,
              signatoryName: signatoryName.trim(),
              piecesDelivered: Number(piecesDelivered || 0),
              signatureImageKey,
              signedStatePhotoKey: photoKey || undefined,
            },
            ...action,
          });
          resetForm();
          toast('Delivery proof saved – will upload when online', 'info');
          navigate('/crew');
          return;
        } catch (queueErr) {
          setError((queueErr as Error).message ?? 'Could not save the signature on this phone');
        }
      } else {
        const apiErr = err as { message?: string };
        setError(apiErr.message ?? 'Could not save the signature. Try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <CrewLayout>
      <OfflineIndicator />
      <div className="crew-content">
        <Nav items={crewNavItems} variant="crew" />

        <div className="pod-page">
          <h2 className="crew-title">Delivery signature</h2>

          {!trip ? (
            <p className="crew-muted">No active trip.</p>
          ) : eligibleStops.length === 0 ? (
            <p className="crew-muted">This trip has no pickup or delivery stops to sign for.</p>
          ) : (
            <>
              <div className="pod-stop-select">
                <label className="crew-sr" htmlFor="pod-stop">Stop</label>
                <select id="pod-stop" className="crew-select" value={activeStopId} onChange={(e) => setSelectedStopId(e.target.value)}>
                  {eligibleStops.map((s) => (
                    <option key={s.id} value={s.id}>
                      Stop #{s.sequence} · {stopTypeLabel(s.type)} · {s.address}
                    </option>
                  ))}
                </select>
              </div>

              {podQueued && !isSigned ? (
                <div className="crew-card pod-sealed">
                  <span className="crew-chip crew-chip-amber">Saved on phone</span>
                  <p>Delivery proof saved – will upload when online. You can go on to the next step.</p>
                  <CrewActionBar>
                    <Button type="button" variant="crew" size="crew" fullWidth onClick={() => navigate('/crew')}>
                      Back to trip
                    </Button>
                  </CrewActionBar>
                </div>
              ) : podLoading ? (
                <div className="loading-center" style={{ padding: 'var(--space-xl)' }}><div className="spinner" /></div>
              ) : (
                <>
                  {isSigned && !showCorrectionForm ? (
                    <div className="crew-card pod-sealed">
                      <span className="crew-chip crew-chip-ok">Signed</span>
                      <dl className="pod-detail">
                        <dt>Proof no.</dt><dd>{pod?.podNumber}</dd>
                        <dt>Signed by</dt><dd>{pod?.signatoryName}</dd>
                        <dt>Pieces</dt><dd className="crew-num">{pod?.piecesDelivered}</dd>
                        <dt>Signed at</dt><dd>{pod ? formatDateTime(pod.generatedAt) : '—'}</dd>
                        {pod?.correctsPodId && <><dt>Note</dt><dd>Correction of an earlier signature</dd></>}
                      </dl>
                      <Button type="button" variant="secondary" fullWidth onClick={() => setShowCorrectionForm(true)}>
                        Sign again (correction)
                      </Button>
                      <CrewActionBar>
                        <Button type="button" variant="crew" size="crew" fullWidth onClick={() => navigate('/crew')}>
                          Back to trip
                        </Button>
                      </CrewActionBar>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="pod-form">
                      {showCorrectionForm && (
                        <CrewMessage kind="warn">
                          This saves a new signature. The first one stays on record and is not changed.
                        </CrewMessage>
                      )}
                      <div className={`pod-fields ${pod ? 'single' : ''}`}>
                        {!pod && (
                          <Input
                            id="pieces-delivered"
                            label="Pieces"
                            aria-label="Pieces delivered"
                            type="number"
                            inputMode="numeric"
                            min="0"
                            value={piecesDelivered}
                            onChange={(e) => setPiecesDelivered(e.target.value)}
                            required
                          />
                        )}
                        <Input
                          id="signatory-name"
                          label="Signatory name"
                          value={signatoryName}
                          onChange={(e) => setSignatoryName(e.target.value)}
                          placeholder="Full name"
                          autoComplete="name"
                          required
                        />
                      </div>
                      <div className="pod-pad">
                        <span className="crew-sr">Signature</span>
                        <SignaturePad ref={sigPadRef} height={padHeight} clearLabel="Clear signature" />
                      </div>
                      {error && <div className="pod-message error crew-msg crew-msg-error" role="alert">{error}</div>}
                      <div className="form-group">
                        <span className="crew-field-label">Photo (optional)</span>
                        <label className={`crew-photo-btn ${photoLoading || submitLoading ? 'is-disabled' : ''}`}>
                          <CrewIcon name="camera" />
                          {photoKey ? 'Retake photo' : 'Take photo'}
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            aria-label="Photo (optional)"
                            onChange={(e) => void handlePhotoChange(e)}
                            disabled={photoLoading || submitLoading}
                          />
                        </label>
                        {photoLoading && <span className="crew-photo-status waiting">Preparing photo…</span>}
                        {!photoLoading && photoKey && <span className="crew-photo-status">Photo attached</span>}
                      </div>

                      {showCorrectionForm && (
                        <Button type="button" variant="secondary" fullWidth onClick={() => setShowCorrectionForm(false)}>
                          Cancel
                        </Button>
                      )}
                      <CrewActionBar>
                        {photoLoading && <p className="pod-wait-hint">Wait for the photo to finish preparing…</p>}
                        <Button
                          type="submit"
                          variant="crew"
                          size="crew"
                          fullWidth
                          loading={submitLoading}
                          disabled={submitLoading || photoLoading}
                        >
                          Save signature
                        </Button>
                      </CrewActionBar>
                    </form>
                  )}
                  {message && <CrewMessage kind="ok">{message}</CrewMessage>}
                </>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        .pod-stop-select { margin-bottom: var(--crew-gap); }
        .pod-fields { display: grid; grid-template-columns: minmax(84px, 2fr) minmax(0, 5fr); gap: var(--cs-3); }
        .pod-fields.single { grid-template-columns: minmax(0, 1fr); }
        .pod-fields .input-group { min-width: 0; }
        .pod-fields .input-label { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .pod-pad { margin-bottom: var(--crew-gap); }
        .crew-shell .signature-canvas { border: 2px solid var(--crew-outline); border-radius: var(--crew-r); }
        .crew-shell .signature-placeholder { color: var(--crew-on-white-muted); font-size: 1.375rem; font-weight: 600; }
        .pod-sealed { display: flex; flex-direction: column; align-items: flex-start; gap: var(--cs-3); }
        .pod-sealed p { margin: 0; }
        .pod-detail {
          display: grid;
          grid-template-columns: auto minmax(0, 1fr);
          gap: var(--cs-1) var(--cs-4);
          margin: 0;
          width: 100%;
        }
        .pod-detail dt { color: var(--crew-muted); }
        .pod-detail dd { margin: 0; font-weight: 600; overflow-wrap: anywhere; }
        .pod-wait-hint { margin: 0; text-align: center; color: var(--crew-muted); font-size: var(--crew-fs-support); }
      `}</style>
    </CrewLayout>
  );
}
